/**
 * Overlay Store - Centralized state management for modals/overlays
 * 
 * Prevents duplicate overlays and ensures proper cleanup
 * Uses React Context (no external dependencies)
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface OverlayState {
  openOverlays: Set<string>;
  activeOverlay: string | null;
  registerOverlay: (id: string, onClose?: () => void) => void;
  unregisterOverlay: (id: string) => void;
  setActiveOverlay: (id: string | null) => void;
  closeAllOverlays: () => void;
}

const OverlayContext = createContext<OverlayState | null>(null);

export const OverlayProvider = ({ children }: { children: ReactNode }) => {
  const [openOverlays, setOpenOverlays] = useState<Set<string>>(new Set());
  const [activeOverlay, setActiveOverlay] = useState<string | null>(null);
  const overlayCallbacksRef = React.useRef<Map<string, () => void>>(new Map());

  /**
   * V3: Fixed race condition - add overlays instead of replacing all
   * Each overlay manages its own lifecycle now
   */
  const registerOverlay = useCallback((id: string, onClose?: () => void) => {
    setOpenOverlays((prev) => {
      const newSet = new Set(prev);
      newSet.add(id); // Add instead of replacing
      return newSet;
    });
    setActiveOverlay(id);
    if (onClose) {
      overlayCallbacksRef.current.set(id, onClose);
    }
  }, []);

  const unregisterOverlay = useCallback((id: string) => {
    setOpenOverlays((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    setActiveOverlay((prev) => (prev === id ? null : prev));
    overlayCallbacksRef.current.delete(id);
  }, []);

  const closeAllOverlays = useCallback(() => {
    overlayCallbacksRef.current.forEach((closeCallback) => {
      closeCallback();
    });
    setOpenOverlays(new Set());
    setActiveOverlay(null);
    overlayCallbacksRef.current.clear();
  }, []);

  return (
    <OverlayContext.Provider
      value={{
        openOverlays,
        activeOverlay,
        registerOverlay,
        unregisterOverlay,
        setActiveOverlay,
        closeAllOverlays,
      }}
    >
      {children}
    </OverlayContext.Provider>
  );
};

export const useOverlayStore = (): OverlayState => {
  const context = useContext(OverlayContext);
  if (!context) {
    // Fallback if provider is not available
    return {
      openOverlays: new Set(),
      activeOverlay: null,
      registerOverlay: () => {},
      unregisterOverlay: () => {},
      setActiveOverlay: () => {},
      closeAllOverlays: () => {},
    };
  }
  return context;
};
